import { assert } from '../shared/assert.js';

const sessionVariables = {
  USER: 'user',
}

const TOKEN_METADATA_KEY = 'ejm_token_metadata';
const REFRESH_WARNING_THRESHOLD = 24 * 60 * 60 * 1000; // 1 day in ms
const ACCESS_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes before expiry

/** @param {{user: User}} param */
export function setSessionData({ user }) {
  // JWT: Only store user data, not tokens or expiry
  // Tokens are in HttpOnly cookies, expiry is managed server-side
  if (user) setSessionUser(user)
}

/**
 * @param {User} user a logged in user
 * @returns {SessionUser} - returns the just set user
 */
export function setSessionUser({ userId, kind, email }) {
  /** @type {SessionUser} */
  const sessionUser = { userId, kind, email }

  window.sessionStorage.setItem(sessionVariables.USER, JSON.stringify(sessionUser))
  return getSessionUser()
}

/** @returns {SessionUser | null} */
export function getSessionUser() {
  const userStr = window.sessionStorage.getItem(sessionVariables.USER)
  const user = userStr ? JSON.parse(userStr) : null
  return user
}

export function sessionUserIsAdmin() {
  return getSessionUser()?.kind === 'Administrator'
}

export function clearSession() {
  window.sessionStorage.clear()
  localStorage.removeItem(TOKEN_METADATA_KEY);
  // Note: JWT cookies are cleared server-side or by browser expiry
}

export function deleteSessionUser() {
  window.sessionStorage.removeItem(sessionVariables.USER)
}

/**
 * Initialize session from JWT cookie if available
 * This prevents the "Please log in" toast on page refresh when JWT is valid
 * @returns {Promise<boolean>} true if session was restored, false otherwise
 */
export async function initializeSessionFromJWT() {
  try {
    // Dynamic import to avoid circular dependencies
    const { getCurrentUser } = await import('../services/ejm-api.js')
    const user = await getCurrentUser()
    
    if (user && user.userId) {
      setSessionUser(user)
      return true
    }
  } catch (error) {
    console.error('📦 SESSION: No valid JWT cookie found or error occurred:', error.message)
  }
  
  return false
}


/** @returns {boolean} */
export function sessionIsExpired() {
  // JWT: Session expiry is managed by HttpOnly cookies
  // We only check if user exists in session
  const user = getSessionUser()
  
  if (!user) {
    console.log('📦 SESSION: No user in session, treating as expired')
    return true
  }
  
  // If user exists, session is valid (cookie expiry handled by browser)
  console.log('📦 SESSION: JWT mode - user exists, session valid')
  return false
}

/**
 * Store token metadata in localStorage
 * @param {TokenMetadata} metadata 
 * @returns {TokenMetadata | null}
 */
export function setTokenMetadata(metadata) {
    localStorage.setItem(TOKEN_METADATA_KEY, JSON.stringify(metadata));

    return getTokenMetadata();
}

/**
 * Get token metadata from storage
 * @returns {TokenMetadata | null}
 */
export function getTokenMetadata() {
    const stored = localStorage.getItem(TOKEN_METADATA_KEY);
    return stored ? JSON.parse(stored) : null;
}

/**
 * Check if access token is expired or about to expire
 * @returns {boolean}
 */
export function isAccessTokenExpired() {
  const metadata = getTokenMetadata();
  if (!metadata) return true;
  
  const now = Date.now();
  const expiry = metadata.accessExpiry;
  
  // Consider expired if within buffer time
  return now >= (expiry - ACCESS_REFRESH_BUFFER);
}

/**
 * Check if refresh token is expired
 * @returns {boolean}
 */
export function isRefreshTokenExpired() {
  const metadata = getTokenMetadata();
  if (!metadata) return true;
  
  return Date.now() >= metadata.refreshExpiry;
}

/**
 * Check if refresh token is expiring soon (within 1 day)
 * @returns {boolean}
 */
export function isRefreshTokenExpiringSoon() {
  const metadata = getTokenMetadata();
  if (!metadata) return false;
  
  const timeUntilExpiry = metadata.refreshExpiry - Date.now();
  return timeUntilExpiry <= REFRESH_WARNING_THRESHOLD && timeUntilExpiry > 0;
}

/**
 * Get time until access token expires
 * @returns {number} Milliseconds until expiry (negative if expired)
 */
export function getTimeUntilAccessExpiry() {
  const metadata = getTokenMetadata();
  if (!metadata) return -1;
  
  return metadata.accessExpiry - Date.now();
}

/**
 * Get time until refresh token expires
 * @returns {number} Milliseconds until expiry (negative if expired)
 */
export function getTimeUntilRefreshExpiry() {
  const metadata = getTokenMetadata();
  if (!metadata) return -1;
  
  return metadata.refreshExpiry - Date.now();
}

/**
 * Calculate next refresh time with jitter
 * @returns {number} Milliseconds until next refresh
 */
export function calculateNextRefreshTime() {
  const timeUntilExpiry = getTimeUntilAccessExpiry();
  
  if (timeUntilExpiry <= 0) {
    // Already expired, refresh immediately
    return 0;
  }
  
  // Refresh 5 minutes before expiry with 0-60 second jitter
  const baseTime = timeUntilExpiry - ACCESS_REFRESH_BUFFER;
  const jitter = Math.random() * 60 * 1000; // 0-60 seconds
  
  return Math.max(0, baseTime + jitter);
}

/**
 * Mark user as authenticated
 */
export function markAsAuthenticated() {
  const metadata = getTokenMetadata();

  if (assert(!!metadata, 'Cannot mark user as authenticated when no token metadata exists')) {
     setTokenMetadata({ ...getTokenMetadata(), isAuthenticated: true });
    return getTokenMetadata();
  }
}

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export function isAuthenticatedUser() {
  return getTokenMetadata()?.isAuthenticated === true;
}

/**
 * Clear all auth data on logout
 */
export function clearAuthData() {
  clearSession(); // Existing function
  localStorage.removeItem(TOKEN_METADATA_KEY);
}

